const React = require('react');
const {
    useEffect,
    useRef
} = React;
const { connect } = require('react-redux');
const { context } = require('app-context');
const Frame = require('../../components/Chat/Frame');
const {
    canLoadDirectMessageChat,
    dependencyPropTypes,
    hasDirectMessageDependencies
} = require('./direct-message-dependencies');

const selectors = context.selectors.all;
const actions   = context.actions;
const redux     = context.redux.hooks;
const handlers  = context.handlers;

const internals = {};

internals.USER_DETAILS_FRESH_MS = 1000;

const DirectMessageFrame = (props) => {

    const {
        onRequestUserDetails,
        otherUserId,
        shouldRequestUserDetails,
        userDetailsFetchedAt,
        ...frameProps
    } = props;

    const shouldRefreshUserDetails = shouldRequestUserDetails
        || !userDetailsFetchedAt
        || Date.now() - userDetailsFetchedAt > internals.USER_DETAILS_FRESH_MS;
    const requestedUserDetailsRef = useRef(null);

    useEffect(() => {

        const id = otherUserId !== undefined && otherUserId !== null ? String(otherUserId) : null;

        if (!shouldRefreshUserDetails || !id || requestedUserDetailsRef.current === id) {
            return undefined;
        }

        requestedUserDetailsRef.current = id;
        onRequestUserDetails({ id });
    }, [onRequestUserDetails, otherUserId, shouldRefreshUserDetails]);

    return React.createElement(Frame, {
        ...frameProps,
        key: frameProps.sid
    });
};

const DirectMessageDetailPage = connect(
    (state, { match: { params } }) => {

        const sid = params.dmSid || params.sid;
        const currentUser = selectors.getCurrentUser(state)?.user();
        const groupUsers = selectors.getDmUsers_forChat(state, sid) || [];
        const userDetails = selectors.getUserDetails_forChat(state, sid);

        const otherUser = groupUsers.find((user) => {

            return user?.id !== undefined
                && currentUser?.id !== undefined
                && String(user.id) !== String(currentUser.id);
        });

        const otherUserId = otherUser?.id;
        const hasOtherUserId = otherUserId !== undefined && otherUserId !== null;
        const otherUserDetails = hasOtherUserId ? selectors.getUser_forDetails(state, otherUserId) : null;
        const otherUserExtension = hasOtherUserId ? state.dataFetching?.entities?.userExtensions?.[otherUserId] : null;
        const shouldRequestUserDetails = hasOtherUserId && (
            !otherUserDetails
            || !otherUserExtension
            || !otherUserDetails.similarities
        );

        const chatUserDetails = userDetails ? {
            ...userDetails,
            interests: userDetails.interests?.length ? userDetails.interests : otherUser?.interests || [],
            passionInterests: userDetails.passionInterests?.length ? userDetails.passionInterests : otherUser?.passionInterests || []
        } : userDetails;

        const categories = selectors.getCategories(state);

        const usersNameMap = groupUsers.reduce((collector, user) => {

            collector[user.id] = `${user.firstName} ${user.lastName}`;
            return collector;
        }, {});

        const baseRolePermissions = selectors.getCurrentUserRolePermissions(state);
        const dmRolePermissions = {
            ...baseRolePermissions,
            canPinMessage: false
        };

        const frameProps = {
            header: selectors.getName_forChat(state, sid),
            userDetails: chatUserDetails,
            classId: null,
            messages: selectors.getMessages_forChat(state, sid) || [],
            hasUnreadMessages: selectors.hasUnreadMessages(state, sid),
            emptyChatInfo: selectors.getInfoMessage_forEmptyUserChat(state, sid),
            users: groupUsers,
            usersNameMap,
            otherUserId,
            shouldRequestUserDetails,
            userDetailsFetchedAt: hasOtherUserId ? selectors.getUserDetailsFetchedAt(state, otherUserId) : null,
            onSetLastReadMessageIndex: ({ messageIndex }) => redux.dispatch(actions.communication.setLastReadMessageIndex({ channelSid: sid, messageIndex })),
            onRequestMessages: ({ initial } = {}) => {

                const fetchMessages = initial
                    ? actions.dataFetching.fetchMessages.firstPage
                    : actions.dataFetching.fetchMessages.nextPage;

                return redux.dispatch(fetchMessages({ channelSid: sid }));
            },
            ga: {
                onSubmitMessage: ['sent peer message', 'peer: sent message']
            },
            isOffline: selectors.getIsOffline(state),
            includeEveryoneMention: false,
            chatText: selectors.getChatTextBySid(state, sid) || '',
            rolePermissions: dmRolePermissions,
            rolesInteractions: selectors.getCurrentUserRoleInteractions(state),
            role: selectors.getCurrentUserRole(state),
            sid,
            announcementConversation: null,
            isAnnouncement: false,
            categories,
            variant: 'dm',
            useProfanityFilter: selectors.getShouldUseProfanityFilter(state),
            conversationStarters: selectors.getAllConversationStarters(state),
            userIsInClass: null,
            currentUser,
            getCurrentListeningChannel: handlers.communication.getCurrentChannelSid,
            startListeningChannel: handlers.communication.startListening,
            stopListeningChannel: handlers.communication.stopListening,
            collapseHeaders: selectors.getCollapseHeaders_forDms(state),
            hasPrevPage: selectors.getHasPrevPage(state),
            schoolEnableReactions: selectors.getSchoolEnableReactions(state),
            schoolEnableDmGifs: selectors.getSchoolEnableDmGifs(state),
            debug: selectors.getDebug(state).chat
        };

        return {
            ...frameProps,
            canLoadChat: canLoadDirectMessageChat(frameProps),
            dependenciesReady: hasDirectMessageDependencies(frameProps)
        };
    }, {
        onSubmitMessage: actions.communication.sendMessage,
        onSaveMessage: actions.communication.updateMessageBody,
        onModerateMessage: actions.communication.moderateMessage,
        onPinMessage: actions.communication.pinMessage,
        onRemoveMessage: actions.communication.removeMessage,
        onRemoveOwnMessage: actions.communication.removeOwnMessage,
        onFlagInappropriate: actions.communication.flagMessageAsInappropriate,
        onAddReaction: actions.chats.addReaction,
        onRemoveReaction: actions.chats.removeReaction,
        setLastReadMessageIndex_byConversationSid: actions.communication.setLastReadMessageIndex_byConversationSid,
        showNotification: (msg) => actions.alerts.notification.push({ message: msg }),
        setCollapseHeaders: (setting) => actions.chats.setCollapseHeadersDms({ setting }),
        onUploadChatImage: actions.communication.uploadChatImage,
        onRequestUserDetails: actions.dataFetching.fetchUser
    }
)(DirectMessageFrame);

DirectMessageDetailPage.dependencyPropTypes = dependencyPropTypes;

module.exports = DirectMessageDetailPage;
